import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { 
  ArrowLeftIcon,
  PlusIcon, 
  TrashIcon,
  MagnifyingGlassIcon,
  UserIcon,
  CalendarDaysIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { useAppData } from '../../contexts/AppDataContext';
import { useDocumentTypes } from '../../contexts/DocumentTypesContext';
import { useDocuments } from '../../contexts/DocumentsContext';

const SaleForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const isEditMode = Boolean(id);
  
  const { 
    addSale, 
    updateSale, 
    getSaleById, 
    getProducts, 
    getCustomers, 
    getWarehouses,
    fetchAllData 
  } = useAppData();
  
  // Get document types from context
  const { documentTypes: allDocumentTypes, getDocumentTypesByCategory } = useDocumentTypes();
  const { documents, refreshDocuments, createDocument } = useDocuments();
  
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showProductSearch, setShowProductSearch] = useState(false);
  
  const [saleData, setSaleData] = useState({
    invoiceNumber: '',
    date: new Date().toISOString().split('T')[0],
    customerId: '',
    warehouseId: '', // Ajouter l'entrepôt source
    reference: '',
    documentType: 'facture-vente',
    paymentMethod: 'especes',
    paymentStatus: 'completed',
    status: 'completed',
    dueDate: '',
    notes: '',
    items: [],
    discount: 0,
    tax: 19 // TVA standard en Algérie
  });
  
  // Données pour les clients, produits et entrepôts depuis le contexte
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  
  // Helper pour afficher un nom cohérent (entreprise ou prénom nom)
  const getEntityName = (ent) => {
    if (!ent) return '';
    const full = `${ent.firstName || ''} ${ent.lastName || ''}`.trim();
    if (full) return full;
    if (ent.name && ent.name.trim() !== '') return ent.name;
    if (ent.company && ent.company.trim() !== '') return ent.company;
    return String(ent.id || '');
  };

  // Charger les données des clients, produits et entrepôts (en temps réel depuis le contexte)
  useEffect(() => {
    const cs = getCustomers();
    const sortedCustomers = [...cs].sort((a, b) => getEntityName(a).toLowerCase().localeCompare(getEntityName(b).toLowerCase()));
    setCustomers(sortedCustomers);
    setProducts(getProducts());
    setWarehouses(getWarehouses());
  }, [getCustomers, getProducts, getWarehouses]);

  // Si navigation depuis une autre page avec un client choisi, le pré-sélectionner
  useEffect(() => {
    if (location.state && location.state.customerId) {
      const selected = getCustomers().find(c => c.id === location.state.customerId);
      if (selected) {
        setSaleData(prev => ({ ...prev, customerId: selected.id }));
        toast.success(`Client ${getEntityName(selected)} sélectionné`);
      }
    }
  }, [location.state, getCustomers]);

  // Generate invoice number based on document type
  const generateInvoiceNumber = (documentType) => {
    const prefixMap = {
      'facture-vente': 'FAC',
      'bon-livraison': 'BL'
      // 'devis' removed as it's not in the documents page
    };
    
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const prefix = prefixMap[documentType] || 'VNT';
    
    return `${prefix}-${year}-${month}-${Math.floor(Math.random() * 900) + 100}`;
  };
  
  // Update invoice number when document type changes
  useEffect(() => {
    if (!isEditMode) {
      const invoiceNumber = generateInvoiceNumber(saleData.documentType);
      
      setSaleData(prev => ({
        ...prev,
        invoiceNumber
      }));
    }
  }, [isEditMode, saleData.documentType]);

  useEffect(() => {
    // Si en mode édition, charger les données de la vente
    if (isEditMode) {
      setIsLoading(true);
      
      try {
        // Récupérer la vente par ID
        const existingSale = getSaleById(id);
        
        if (existingSale) {
          setSaleData(existingSale);
        } else {
          toast.error('Vente introuvable');
          navigate('/sales');
        }
      } catch (error) {
        console.error('Erreur lors du chargement de la vente:', error);
        toast.error('Erreur lors du chargement de la vente');
        navigate('/sales');
      } finally {
        setIsLoading(false);
      }
    }
  }, [isEditMode, id, getSaleById, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSaleData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddItem = () => {
    setShowProductSearch(true);
  };

  const handleSelectProduct = (product) => {
    // Vérifier si le produit a du stock
    if (product.stock <= 0) {
      toast.error('Ce produit est en rupture de stock');
      return;
    }
    
    const newItem = {
      id: product.id,
      name: product.name,
      sku: product.sku,
      quantity: 1,
      price: Number(product.price ?? 0),
      total: Number(product.price ?? 0),
      unit: product.unit,
      stock: product.stock
    };
    
    setSaleData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
    
    setShowProductSearch(false);
    setSearchQuery('');
    calculateTotals();
  };

  const handleRemoveItem = (index) => {
    setSaleData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
    calculateTotals();
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...saleData.items];
    
    if (field === 'quantity') {
      value = Number(value);
      
      // Vérifier que la quantité ne dépasse pas le stock disponible
      if (value > updatedItems[index].stock) {
        toast.error(`Stock disponible: ${updatedItems[index].stock} unités`);
        value = updatedItems[index].stock;
      }
      
      updatedItems[index][field] = value;
      const q = Number(updatedItems[index].quantity ?? 0);
      const p = Number(updatedItems[index].price ?? 0);
      updatedItems[index].total = (isNaN(q) ? 0 : q) * (isNaN(p) ? 0 : p);
    } else if (field === 'price') {
      value = Number(value);
      updatedItems[index][field] = isNaN(value) ? 0 : value;
      const q = Number(updatedItems[index].quantity ?? 0);
      const p = Number(updatedItems[index].price ?? 0);
      updatedItems[index].total = (isNaN(q) ? 0 : q) * (isNaN(p) ? 0 : p);
    } else {
      updatedItems[index][field] = value;
    }
    
    setSaleData(prev => ({
      ...prev,
      items: updatedItems
    }));
    
    calculateTotals();
  };

  const handleDiscountChange = (e) => {
    const discountValue = parseFloat(e.target.value) || 0;
    setSaleData(prev => ({
      ...prev,
      discount: discountValue
    }));
    calculateTotals();
  };

  const calculateTotals = () => {
    // Dans une application réelle, cette fonction mettrait à jour les sous-totaux, taxes, etc.
  };

  const filteredProducts = searchQuery.trim() === '' 
    ? products.slice(0, 50) // Show the first 50 products when no search is performed
    : products.filter(product => 
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.sku.toLowerCase().includes(searchQuery.toLowerCase())
      );

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation basique
    if (!saleData.customerId) {
      toast.error('Veuillez sélectionner un client');
      return;
    }
    
    if (!saleData.warehouseId) {
      toast.error('Veuillez sélectionner un entrepôt');
      return;
    }
    
    if (saleData.items.length === 0) {
      toast.error('Veuillez ajouter au moins un article à la vente');
      return;
    }
    
    setIsLoading(true);
    
    try {
      let savedSale;
      
      if (isEditMode) {
        // Mise à jour d'une vente existante
        savedSale = await updateSale(saleData.id, saleData);
        toast.success(`La vente ${saleData.invoiceNumber} a été mise à jour`);
      } else {
        // Création d'une nouvelle vente
        savedSale = await addSale(saleData);
        toast.success(`La vente ${saleData.invoiceNumber} a été créée avec succès`);
        
        // Create corresponding document in documents page
        await createCorrespondingSalesDocument(savedSale);
      }
      
      // Rafraîchir les données pour s'assurer que l'UI est à jour
      await fetchAllData();
      
      // Navigation vers la liste des ventes
      navigate('/sales');
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement de la vente:', error);
      toast.error('Erreur lors de l\'enregistrement de la vente');
    } finally {
      setIsLoading(false);
    }
  };

  // Calculer le sous-total
  const subtotal = saleData.items.reduce((sum, item) => {
    const q = Number(item.quantity ?? 0);
    const p = Number(item.price ?? 0);
    const line = Number(item.total ?? (q * p));
    return sum + (isNaN(line) ? 0 : line);
  }, 0);
  
  // Calculer le montant de la remise
  const discountAmount = (saleData.discount / 100) * subtotal;
  
  // Calculer le montant taxable
  const taxableAmount = subtotal - discountAmount;
  
  // Calculer la TVA
  const taxAmount = (saleData.tax / 100) * taxableAmount;
  
  // Calculer le total
  const totalAmount = taxableAmount + taxAmount;

  // Function to create a corresponding document in the documents page
  const createCorrespondingSalesDocument = async (sale) => {
    try {
      if (!sale) {
        console.error("Cannot create document: Sale data is missing");
        return null;
      }
      
      // Get customer details
      const customer = customers.find(c => c.id === sale.customerId);
      if (!customer) {
        console.error("Cannot create document: Customer not found");
        return null;
      }
      
      // Map sale document type to document type
      const documentTypeMapping = {
        'facture-vente': 'invoice',
        'bon-livraison': 'delivery-note'
      };
      
      const documentType = documentTypeMapping[sale.documentType] || 'invoice';
      
      // Create document data from sale data
      const documentData = {
        type: documentType,
        number: sale.invoiceNumber,
        date: sale.date,
        dueDate: sale.dueDate || null,
        clientId: sale.customerId,
        contactName: getEntityName(customer),
        contactEmail: customer.email || '',
        contactPhone: customer.phone || '',
        contactAddress: customer.address || '',
        items: sale.items.map(item => ({
          productId: item.id || null,
          description: item.name || item.description || '',
          quantity: parseInt(item.quantity) || 1,
          unitPrice: parseFloat(item.price) || 0,
          total: parseFloat(item.total) || 0
        })),
        subtotal: subtotal,
        taxRate: sale.tax || 19,
        taxAmount: taxAmount,
        total: totalAmount,
        notes: sale.notes || null,
        paymentMethod: sale.paymentMethod || null,
        status: sale.paymentStatus === 'completed' ? 'paid' : 'draft'
      };
      
      console.log("Creating corresponding document:", documentData);
      
      // Create the document
      const createdDocument = await createDocument(documentData);
      
      if (createdDocument) {
        toast.success("Document correspondant créé avec succès dans la section Documents");
        return createdDocument;
      } else {
        console.error("Failed to create corresponding document");
        return null;
      }
    } catch (error) {
      console.error("Error creating corresponding document:", error);
      toast.error("Erreur lors de la création du document correspondant");
      return null;
    }
  };

  // Get sales document types from the document system
  const getSalesDocumentTypes = () => {
    // Get sales document types from DocumentTypes context
    const salesDocTypes = getDocumentTypesByCategory('sales') || [];
    
    // Map document types to the format expected by the sales form
    if (salesDocTypes && salesDocTypes.length > 0) {
      return salesDocTypes.map(docType => ({
        id: docType.id === 'invoice' ? 'facture-vente' : 
            docType.id === 'delivery-note' ? 'bon-livraison' : docType.id,
        label: docType.name
      }));
    }
    
    // Fallback to default document types if none found in context
    return [
    { id: 'facture-vente', label: 'Facture de vente' },
      { id: 'bon-livraison', label: 'Bon de livraison' }
      // No 'devis' option as it's not in the documents page
  ];
  };
  
  // Get document types for the dropdown
  const documentTypes = getSalesDocumentTypes();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/sales')}
            className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
          >
            <ArrowLeftIcon className="h-6 w-6 text-white" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-white">
              {isEditMode ? 'Modifier la vente' : 'Nouvelle vente'}
            </h1>
            <p className="text-white/60 mt-1">
              {isEditMode ? 'Modifier les informations de la vente' : 'Créez une nouvelle vente dans votre système'}
            </p>
          </div>
        </div>
      </div>

      {/* Formulaire */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white"></div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Informations d'en-tête */}
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <h2 className="text-xl font-semibold text-white mb-6">Informations de la vente</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="invoiceNumber" className="block text-white font-medium mb-1">
                    Numéro de facture
                  </label>
                  <input
                    type="text"
                    id="invoiceNumber"
                    name="invoiceNumber"
                    value={saleData.invoiceNumber}
                    onChange={handleInputChange}
                    className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Généré automatiquement"
                    readOnly
                  />
                </div>
                
                <div>
                  <label htmlFor="date" className="block text-white font-medium mb-1">
                    Date de vente <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <CalendarDaysIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                    <input
                      type="date"
                      id="date"
                      name="date"
                      value={saleData.date}
                      onChange={handleInputChange}
                      className="w-full pl-10 p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-green-500"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="customerId" className="block text-white font-medium mb-1">
                    Client <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                    <select
                      id="customerId"
                      name="customerId"
                      value={saleData.customerId}
                      onChange={handleInputChange}
                      className="w-full pl-10 p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                      required
                    >
                      <option value="">-- Sélectionner un client --</option>
                      {customers.map(customer => (
                        <option key={customer.id} value={customer.id}>{getEntityName(customer)}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div>
                  <label htmlFor="warehouseId" className="block text-white font-medium mb-1">
                    Entrepôt <span className="text-red-400">*</span>
                  </label>
                  <select
                    id="warehouseId"
                    name="warehouseId"
                    value={saleData.warehouseId}
                    onChange={handleInputChange}
                    className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  >
                    <option value="">-- Sélectionner un entrepôt --</option>
                    {warehouses.map(warehouse => (
                      <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label htmlFor="reference" className="block text-white font-medium mb-1">
                    Référence externe
                  </label>
                  <input
                    type="text"
                    id="reference"
                    name="reference"
                    value={saleData.reference}
                    onChange={handleInputChange}
                    className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Ex: Commande client, référence interne..."
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                <div>
                  <label htmlFor="documentType" className="block text-white font-medium mb-1">
                    Type de document
                  </label>
                  <select
                    id="documentType"
                    name="documentType"
                    value={saleData.documentType}
                    onChange={handleInputChange}
                    className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    {/* Only show document types from the Documents page */}
                    {/* Hard-code only the allowed types to ensure no 'Devis' appears */}
                    <option value="facture-vente">Facture de vente</option>
                    <option value="bon-livraison">Bon de livraison</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="paymentMethod" className="block text-white font-medium mb-1">
                    Méthode de paiement
                  </label>
                  <select
                    id="paymentMethod"
                    name="paymentMethod"
                    value={saleData.paymentMethod}
                    onChange={handleInputChange}
                    className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="especes">Espèces</option>
                    <option value="cheque">Chèque</option>
                    <option value="virement">Virement bancaire</option>
                    <option value="carte">Carte bancaire</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="paymentStatus" className="block text-white font-medium mb-1">
                    Statut du paiement
                  </label>
                  <select
                    id="paymentStatus"
                    name="paymentStatus"
                    value={saleData.paymentStatus}
                    onChange={handleInputChange}
                    className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="completed">Payé</option>
                    <option value="pending">En attente</option>
                    <option value="partial">Partiel</option>
                    <option value="cancelled">Annulé</option>
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <div>
                  <label htmlFor="status" className="block text-white font-medium mb-1">
                    Statut de la vente
                  </label>
                  <select
                    id="status"
                    name="status"
                    value={saleData.status}
                    onChange={handleInputChange}
                    className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="completed">Complétée</option>
                    <option value="pending">En attente</option>
                    <option value="cancelled">Annulée</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="dueDate" className="block text-white font-medium mb-1">
                    Date d'échéance
                  </label>
                  <input
                    type="date"
                    id="dueDate"
                    name="dueDate"
                    value={saleData.dueDate}
                    onChange={handleInputChange}
                    className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>
            </div>

            {/* Articles */}
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-white">Articles</h2>
                
                <button
                  type="button"
                  onClick={handleAddItem}
                  className="bg-white/10 hover:bg-white/20 transition-colors py-2 px-4 rounded-lg text-white flex items-center space-x-2"
                >
                  <PlusIcon className="h-5 w-5" />
                  <span>Ajouter un article</span>
                </button>
              </div>
              
              {showProductSearch && (
                <div className="mb-6 bg-white/5 rounded-xl p-4 border border-white/10">
                  <div className="flex items-center mb-4">
                    <h3 className="text-white font-medium">Rechercher un produit</h3>
                    <button
                      type="button"
                      onClick={() => setShowProductSearch(false)}
                      className="ml-auto p-1.5 bg-white/10 rounded-lg hover:bg-white/20 text-white"
                    >
                      <XCircleIcon className="h-5 w-5" />
                    </button>
                  </div>
                  
                  <div className="relative mb-4">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Rechercher par nom ou référence"
                      className="w-full pl-10 p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                  
                  <div className="max-h-64 overflow-y-auto bg-white/5 rounded-lg">
                    <table className="min-w-full divide-y divide-white/10">
                      <thead className="bg-white/5">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-white/70">Référence</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-white/70">Produit</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-white/70">Prix</th>
                          <th className="px-4 py-2 text-center text-xs font-medium text-white/70">Stock</th>
                          <th className="px-4 py-2 text-center text-xs font-medium text-white/70">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredProducts.length > 0 ? (
                          filteredProducts.map((product) => (
                            <tr key={product.id} className="hover:bg-white/10">
                              <td className="px-4 py-2 text-sm text-white/80">{product.sku || '-'}</td>
                              <td className="px-4 py-2 text-sm text-white">{product.name || 'Produit sans nom'}</td>
                              <td className="px-4 py-2 text-sm text-white text-right">{(product.price || 0).toLocaleString()} DA</td>
                              <td className={`px-4 py-2 text-sm text-center ${(product.stock || 0) > 0 ? 'text-green-400' : 'text-red-400'}`}>
                                {product.stock || 0}
                              </td>
                              <td className="px-4 py-2 text-center">
                                <button
                                  type="button"
                                  onClick={() => handleSelectProduct(product)}
                                  className={`p-1.5 ${(product.stock || 0) > 0 ? 'bg-green-500/20 hover:bg-green-500/30 text-green-400' : 'bg-red-500/20 text-red-400 opacity-50 cursor-not-allowed'}`}
                                  disabled={(product.stock || 0) <= 0}
                                  title={(product.stock || 0) > 0 ? "Ajouter" : "Produit en rupture de stock"}
                                  style={{ borderRadius: '8px' }}
                                >
                                  <PlusIcon className="h-4 w-4" />
                                </button>
                              </td>
                            </tr>
                          ))
                        ) : searchQuery.trim() !== '' ? (
                          <tr>
                            <td colSpan="5" className="px-4 py-4 text-center text-white/60">
                              Aucun produit trouvé
                            </td>
                          </tr>
                        ) : (
                          <tr>
                            <td colSpan="5" className="px-4 py-4 text-center text-white/60">
                              Chargement des produits...
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              
              {saleData.items.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-white/10">
                    <thead className="bg-white/5">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white/70">Produit</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white/70">Référence</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-white/70">Quantité</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-white/70">Prix unitaire</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-white/70">Total</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-white/70">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/10">
                      {saleData.items.map((item, index) => (
                        <tr key={index}>
                          <td className="px-4 py-3 text-sm text-white">{item.name}</td>
                          <td className="px-4 py-3 text-sm text-white/80">{item.sku}</td>
                          <td className="px-4 py-3 text-sm">
                            <input
                              type="number"
                              min="1"
                              max={item.stock}
                              value={item.quantity}
                              onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                              className="w-20 p-1.5 bg-white/10 border border-white/20 rounded-lg text-white text-center focus:outline-none focus:ring-1 focus:ring-green-500"
                            />
                          </td>
                          <td className="px-4 py-3 text-sm text-white text-right">
                            <input
                              type="number"
                              min="0"
                              value={item.price}
                              onChange={(e) => handleItemChange(index, 'price', e.target.value)}
                              className="w-28 p-1.5 bg-white/10 border border-white/20 rounded-lg text-white text-right focus:outline-none focus:ring-1 focus:ring-green-500"
                            />
                            <span className="ml-1">DA</span>
                          </td>
                          <td className="px-4 py-3 text-sm text-white font-medium text-right">{item.total.toLocaleString()} DA</td>
                          <td className="px-4 py-3 text-center">
                            <button
                              type="button"
                              onClick={() => handleRemoveItem(index)}
                              className="p-1.5 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-400 transition-colors"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 border border-dashed border-white/20 rounded-xl">
                  <DocumentTextIcon className="h-12 w-12 mx-auto text-white/30 mb-2" />
                  <p className="text-white/60">Aucun article ajouté à cette vente.</p>
                  <p className="text-white/50 text-sm mt-1">Cliquez sur "Ajouter un article" pour commencer.</p>
                </div>
              )}

              {saleData.items.length > 0 && (
                <div className="mt-6 flex justify-end">
                  <div className="bg-white/5 rounded-xl p-4 w-80">
                    <div className="space-y-3">
                      <div className="flex justify-between items-center text-white/80">
                        <span>Sous-total:</span>
                        <span>{subtotal.toLocaleString()} DA</span>
                      </div>
                      
                      <div className="flex justify-between items-center text-white/80">
                        <label htmlFor="discount" className="flex items-center">
                          <span>Remise (%):</span>
                        </label>
                        <input
                          type="number"
                          id="discount"
                          name="discount"
                          min="0"
                          max="100"
                          value={saleData.discount}
                          onChange={handleDiscountChange}
                          className="w-16 p-1 bg-white/10 border border-white/20 rounded-lg text-white text-right focus:outline-none focus:ring-1 focus:ring-green-500"
                        />
                      </div>
                      
                      {saleData.discount > 0 && (
                        <div className="flex justify-between items-center text-white/80">
                          <span>Montant remise:</span>
                          <span>-{discountAmount.toLocaleString()} DA</span>
                        </div>
                      )}
                      
                      <div className="flex justify-between items-center text-white/80">
                        <label htmlFor="tax" className="flex items-center">
                          <span>TVA (%):</span>
                        </label>
                        <input
                          type="number"
                          id="tax"
                          name="tax"
                          min="0"
                          max="100"
                          value={saleData.tax}
                          onChange={handleInputChange}
                          className="w-16 p-1 bg-white/10 border border-white/20 rounded-lg text-white text-right focus:outline-none focus:ring-1 focus:ring-green-500"
                        />
                      </div>
                      
                      <div className="flex justify-between items-center text-white/80">
                        <span>Montant TVA:</span>
                        <span>{taxAmount.toLocaleString()} DA</span>
                      </div>
                      
                      <div className="flex justify-between font-bold border-t border-white/10 pt-3 text-white">
                        <span>Total:</span>
                        <span>{totalAmount.toLocaleString()} DA</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <h2 className="text-xl font-semibold text-white mb-4">Notes</h2>
              
              <textarea
                id="notes"
                name="notes"
                value={saleData.notes}
                onChange={handleInputChange}
                rows="4"
                className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="Notes additionnelles sur cette vente..."
              ></textarea>
            </div>
            
            {/* Boutons d'action */}
            <div className="flex justify-end space-x-4 pt-4">
              <button
                type="button"
                onClick={() => navigate('/sales')}
                className="px-6 py-3 bg-white/10 text-white rounded-xl hover:bg-white/20 transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 transition-colors flex items-center"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="animate-spin h-4 w-4 mr-2 border-b-2 border-white rounded-full"></span>
                    Traitement...
                  </>
                ) : isEditMode ? 'Mettre à jour' : 'Enregistrer'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default SaleForm; 